import React, { useState } from 'react';
import Helmet from '../components/Helmet/Helmet';
import CommonSection from '../components/Ui/CommonSection';
import { Container, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import '../styles/services.css';

const signatureServices = [
  {
    icon: 'ri-pencil-ruler-2-line',
    title: '3D Spatial Planning & Styling',
    desc: 'Collaborate one-on-one with a senior interior curator. We translate your architectural floor plans into detailed 3D spatial renders, harmonious material palettes, and lighting guidance.',
    perks: [
      'Comprehensive 3D layout simulation',
      'Curated tactile sample box delivered to your door',
      'Exclusive access to archival color palettes',
    ],
  },
  {
    icon: 'ri-scissors-2-line',
    title: 'Made-to-Measure Customization',
    desc: 'When standard dimensions do not suffice, our master artisans craft bespoke pieces. Modify sectional lengths, select from 80+ Italian full-grain leathers, or request custom wood timber stains.',
    perks: [
      'Custom sizing down to the centimeter',
      '80+ European bouclé, velvet, & leather options',
      'Hand-signed Atelier Certificate of Authenticity',
    ],
  },
  {
    icon: 'ri-truck-line',
    title: 'White-Glove Premier Delivery',
    desc: 'Our specialized white-glove transport team safely delivers your piece directly into your room of choice, handles all necessary precision unboxing & assembly, and removes all packaging responsibly.',
    perks: [
      'Scheduled 2-hour delivery appointment window',
      'In-home room placement & complete assembly',
      'Eco-conscious packaging collection & recycling',
    ],
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Atelier Care & Lifetime Guarantee',
    desc: 'We stand by every handcrafted join and stitch. Benefit from structural frame guarantees, complimentary annual leather conditioning kits, and specialized restorative care for the lifetime of your piece.',
    perks: [
      '10-Year structural internal frame warranty',
      'Complimentary annual conditioning concierge kit',
      'Direct priority access to master repair craftsmen',
    ],
  },
];

const processSteps = [
  {
    step: '01',
    title: 'Consultation',
    desc: 'Share your floor plans, aesthetic desires, and lifestyle needs with our senior design curator.',
  },
  {
    step: '02',
    title: '3D Curation',
    desc: 'Receive tailored 3D renders, fabric swatches, and lighting harmony recommendations.',
  },
  {
    step: '03',
    title: 'Artisan Craft',
    desc: 'Your chosen pieces are handcrafted in our ateliers using certified hardwoods and noble fabrics.',
  },
  {
    step: '04',
    title: 'White-Glove Arrival',
    desc: 'Delivered and assembled seamlessly inside your residence by our white-glove transport crew.',
  },
];

const Services = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    spaceType: 'Living Room / Salon',
    consultationMode: 'Cairo Flagship Showroom (Zamalek)',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Please provide your name and email address.');
      return;
    }

    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      toast.success(
        'Thank you! Your VIP consultation request has been received. Our Senior Concierge will contact you within 24 hours.'
      );
      setFormData({
        name: '',
        email: '',
        phone: '',
        spaceType: 'Living Room / Salon',
        consultationMode: 'Cairo Flagship Showroom (Zamalek)',
        message: '',
      });
    }, 800);
  };

  return (
    <Helmet title="Bespoke Interior Services & Concierge | L'HABILLEUR">
      <CommonSection title="Bespoke Atelier Services" />

      {/* Hero Section */}
      <section className="services__hero-section">
        <Container>
          <span className="about__section-badge">Atelier Tailoring & Curation</span>
          <h2 className="fw-bold mb-3" style={{ color: 'var(--primary-color)', fontSize: '2.5rem' }}>
            Elevate Your Sanctuary Beyond the Ordinary
          </h2>
          <p className="text-muted mx-auto mb-0" style={{ maxWidth: '680px', fontSize: '1.05rem', lineHeight: '1.7' }}>
            From private architectural styling and custom millimeter-tailoring to white-glove assembly and lifetime care, experience the full measure of atelier luxury.
          </p>
        </Container>
      </section>

      {/* 4 Signature Services */}
      <section className="py-5 bg-white">
        <Container className="py-4">
          <Row className="g-4">
            {signatureServices.map((service, idx) => (
              <Col key={idx} lg="6" md="12">
                <div className="services__card">
                  <div className="services__icon-box">
                    <i className={service.icon}></i>
                  </div>
                  <h4 className="services__title">{service.title}</h4>
                  <p className="services__desc">{service.desc}</p>
                  <ul className="services__perks">
                    {service.perks.map((perk, pIdx) => (
                      <li key={pIdx}>
                        <i className="ri-check-line"></i>
                        <span>{perk}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* How It Works Steps */}
      <section className="py-5 bg-light border-top border-bottom">
        <Container className="py-4">
          <div className="text-center mb-5">
            <span className="about__section-badge">The Journey</span>
            <h2 className="fw-bold" style={{ color: 'var(--primary-color)' }}>
              How Our Private Concierge Works
            </h2>
            <p className="text-muted mx-auto" style={{ maxWidth: '600px' }}>
              A seamless, personalized progression from initial sketch to final white-glove placement.
            </p>
          </div>

          <div className="services__process-grid">
            {processSteps.map((step, idx) => (
              <div key={idx} className="services__step-box">
                <div className="services__step-number">{step.step}</div>
                <h5 className="services__step-title">{step.title}</h5>
                <p className="services__step-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Booking Form Section */}
      <section className="services__booking-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg="8" md="10">
              <div className="services__form-card">
                <div className="services__form-header text-center">
                  <span className="about__section-badge">VIP Consultation</span>
                  <h3 className="fw-bold mb-2" style={{ color: 'var(--primary-color)' }}>
                    Request an Interior Styling Session
                  </h3>
                  <p className="text-muted">
                    Schedule a private appointment with our senior design curator at our boutique or via virtual video walkthrough.
                  </p>
                </div>

                <form onSubmit={handleSubmit}>
                  <Row className="g-3">
                    <Col md="6">
                      <div className="services__form-group">
                        <label>Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="e.g. Lady Katherine Vance"
                          required
                        />
                      </div>
                    </Col>

                    <Col md="6">
                      <div className="services__form-group">
                        <label>Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="client@luxuryresidence.com"
                          required
                        />
                      </div>
                    </Col>

                    <Col md="6">
                      <div className="services__form-group">
                        <label>Direct Phone / WhatsApp</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+20 100 000 0000"
                        />
                      </div>
                    </Col>

                    <Col md="6">
                      <div className="services__form-group">
                        <label>Space Type</label>
                        <select
                          name="spaceType"
                          value={formData.spaceType}
                          onChange={handleChange}
                        >
                          <option value="Living Room / Salon">Living Room / Salon</option>
                          <option value="Penthouse Residence">Penthouse Residence</option>
                          <option value="Executive Home Office">Executive Home Office</option>
                          <option value="Master Bedroom Suite">Master Bedroom Suite</option>
                          <option value="Full Villa Architectural Project">Full Villa Architectural Project</option>
                          <option value="Commercial / Boutique Hospitality">Commercial / Boutique Hospitality</option>
                        </select>
                      </div>
                    </Col>

                    <Col md="12">
                      <div className="services__form-group">
                        <label>Preferred Consultation Mode & Location</label>
                        <select
                          name="consultationMode"
                          value={formData.consultationMode}
                          onChange={handleChange}
                        >
                          <option value="Cairo Flagship Showroom (Zamalek)">
                            Cairo Flagship Showroom (Zamalek, Cairo)
                          </option>
                          <option value="Paris Flagship (Place Vendôme)">
                            Paris Flagship (Place Vendôme, Paris)
                          </option>
                          <option value="Dubai Salon (d3 Design District)">
                            Dubai Salon (d3 Design District)
                          </option>
                          <option value="Virtual Private Video Walkthrough">
                            Virtual Private Video Walkthrough (Global)
                          </option>
                        </select>
                      </div>
                    </Col>

                    <Col md="12">
                      <div className="services__form-group">
                        <label>Project Details & Desired Aesthetic (Optional)</label>
                        <textarea
                          rows="4"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Share approximate room dimensions, preferred colors, or timelines..."
                        ></textarea>
                      </div>
                    </Col>

                    <Col md="12" className="text-center mt-3">
                      <button
                        type="submit"
                        className="buy__btn w-100 py-3"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <span>
                            <i className="ri-loader-4-line ri-spin me-2"></i> Confirming VIP Booking...
                          </span>
                        ) : (
                          <span>
                            <i className="ri-calendar-check-line me-2"></i> Book Atelier Consultation
                          </span>
                        )}
                      </button>
                      <small className="d-block text-muted mt-2">
                        Complimentary service &bull; Non-binding &bull; Strictly confidential
                      </small>
                    </Col>
                  </Row>
                </form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </Helmet>
  );
};

export default Services;
